/**
 * Step 7 — contact resolution (owner: contact/counter team), ARCHITECTURE §5 "Counter square":
 *  1. Mirage decoys: any live lunge hitbox touching the opponent's decoy pops it (DECOY_POP, the lunger WHIFFs,
 *     FAKE_SUCCESS 'mirage'). Decoys are never solid and never tackled.
 *  2. Carrier vs defender (LIVE only — no defender attacks during the rush count): hitboxes decide CONTACT (a live
 *     Dive / Leap hitbox, else a plain head-contact GRAB, or the stiff-arm Fist reaching a grabber), then
 *     `counterOutcome(carrierClass, lungeClass)` decides who wins — before any other tackle logic:
 *       TACKLE / COUNTER_TACKLE → Bulldozer (ACTIVE) turns a GRAB/DIVE tackle into BULLDOZE; else the carrier is
 *         TACKLED (hit-stop tackle / counterTackle), the ball stays in his glove (the rules spot it at his x).
 *       WHIFF → SPIN_DODGE (spin i-frames) or HURDLE_CLEAR (airborne carrier over a Dive).
 *       BREAK → TRUCK_BREAK (truck beats a Leap / a standing grab) or a stiff-arm hit (beats a grab only).
 *       NONE  → nothing (bodies push apart in the integrator).
 *     A stiff-arm / truck-burst win over a GRAB needs the grabber in FRONT of the carrier (`carrierClassAgainst`).
 *     Every counter win (BREAK, BULLDOZE, a spin dodging a GRAB) leaves the defender BEATEN (`beatenFrames`):
 *     non-solid and unable to contact-grab until the carrier is past him (radii + contactSlop in the carrier's
 *     attack direction) or MOTION.beatenMaxFrames run out — so a won exchange actually gains ground.
 *  3. Baits that pay off on contact (spin dodge, defender fake dive / fake rush / fast-fall, carrier fakes whose
 *     baited lunge failed), via contact/fx.ts. Power is the rules' job (they read statBaits / TACKLE state).
 */
import {
  CarrierClass,
  CounterOutcome,
  FRAMES,
  HITBOX,
  HITSTOP,
  LungeClass,
  MOTION,
  SPECIALS_TUNING,
  SpecialId,
  counterOutcome,
} from '@hf/shared';
import { BallState, CommitKind, Phase, PlayerState, SpecialPhase } from '../enums';
import { PF, setLock } from '../player/fsm';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';
import {
  Once,
  addHitStop,
  commitFailed,
  defenderPayoffBait,
  fakeSuccess,
  firstThisStep,
  spinDodgeBait,
} from './fx';
import {
  activeLunge,
  boxesTouch,
  canGrab,
  carrierClassAgainst,
  carrierClassOf,
  circleBox,
  hurt,
  hurtboxOf,
  lungeBox,
  lungeBoxOf,
  lungeReachesHeight,
  stiffArmBoxOf,
} from './hitbox';
import { stiffArmActive } from '../player/frames';
import { carrierAttackDir } from '../rules/helpers';

const LUNGE_KIND = ['grab', 'dive', 'leap'] as const;

const emitWhiff = (s: SimState, ev: SimEventSink, p: PlayerSim, lunge: number): void => {
  if (ev && firstThisStep(Once.WHIFF, p.index))
    ev.push({ type: 'WHIFF', tick: s.tick, actor: p.index, x: p.x, y: p.y, kind: LUNGE_KIND[lunge]! });
};

/** Close a lunge as a whiff (no tackle) and let a carrier's feint claim it. */
const lungeFails = (s: SimState, ev: SimEventSink, d: PlayerSim, lunge: number): void => {
  if (lunge === LungeClass.GRAB) return;
  d.flags |= PF.LUNGE_DONE | PF.LUNGE_WHIFF;
  emitWhiff(s, ev, d, lunge);
};

/** Knock `d` away from `c` and stun him. */
const knock = (
  s: SimState,
  ev: SimEventSink,
  c: PlayerSim,
  d: PlayerSim,
  vx: number,
  vy: number,
  frames: number,
): void => {
  const away = d.x > c.x ? 1 : d.x < c.x ? -1 : c.facing;
  d.flags |= PF.LUNGE_DONE;
  setLock(d, PlayerState.STUNNED, frames);
  d.beatenFrames = MOTION.beatenMaxFrames;
  d.beatenTick = s.tick;
  d.vx = away * vx;
  if (vy > d.vy) d.vy = vy;
  if (ev && firstThisStep(Once.STUN, d.index))
    ev.push({ type: 'STUN', tick: s.tick, actor: d.index, x: d.x, y: d.y, frames });
};

// ── Mirage decoys ─────────────────────────────────────────────────────────────────────────────────────

const popDecoys = (s: SimState, ev: SimEventSink): void => {
  for (let i = 0; i < 2; i++) {
    const owner = s.players[i as 0 | 1];
    const sp = owner.special;
    if (!sp.decoyActive) continue;
    const lunger = s.players[i === 0 ? 1 : 0];
    const lunge = activeLunge(lunger);
    if (lunge < 0) continue;
    lungeBoxOf(lunger, lunge as LungeClass, lungeBox);
    circleBox(sp.decoyX, sp.decoyY, owner.stats.headRadius, hurt);
    if (!boxesTouch(lungeBox, hurt)) continue;
    sp.decoyActive = false;
    sp.decoyFrames = 0;
    if (ev)
      ev.push({ type: 'DECOY_POP', tick: s.tick, actor: i, x: sp.decoyX, y: sp.decoyY, by: lunger.index });
    lungeFails(s, ev, lunger, lunge);
    lunger.beatenTick = s.tick; // he lunged at a hologram: pursuit grace (MOTION.pursuit.graceFrames)
    fakeSuccess(s, ev, owner, lunger, 'mirage');
  }
};

// ── Outcomes ──────────────────────────────────────────────────────────────────────────────────────────

const tackle = (
  s: SimState,
  ev: SimEventSink,
  c: PlayerSim,
  d: PlayerSim,
  lunge: number,
  counter: boolean,
  cc: CarrierClass,
): void => {
  d.flags |= PF.LUNGE_DONE;
  setLock(c, PlayerState.TACKLED, FRAMES.tackled.down);
  const dir = c.x > d.x ? 1 : c.x < d.x ? -1 : d.facing;
  c.vx = dir * MOTION.tackledSlide;
  c.iFrames = 0;
  c.burstFrames = 0;
  const r = s.rules;
  if (ev)
    ev.push({
      type: 'TACKLE',
      tick: s.tick,
      actor: d.index,
      x: c.x,
      y: c.y,
      kind: LUNGE_KIND[lunge]!,
      carrier: c.index,
      counter: !counter ? 'none' : cc === CarrierClass.AIR ? 'leap_beats_hurdle' : 'dive_beats_truck',
      yards: (c.x - r.los) * r.attackDir,
    });
  addHitStop(
    s,
    ev,
    d.index,
    counter ? HITSTOP.counterTackle : HITSTOP.tackle,
    counter ? 'counter' : 'tackle',
    c.x,
    c.y,
  );
  defenderPayoffBait(s, ev, d, c, true);
  d.commitKind = CommitKind.NONE; // the commitment worked
};

const bulldozerActive = (c: PlayerSim): boolean =>
  c.special.id === SpecialId.BULLDOZER && c.special.phase === SpecialPhase.ACTIVE;

const bulldoze = (s: SimState, ev: SimEventSink, c: PlayerSim, d: PlayerSim, lunge: number): void => {
  const B = SPECIALS_TUNING.bulldozer;
  const sp = c.special;
  sp.phase = SpecialPhase.SPENT;
  sp.frame = 0;
  sp.timer = 0;
  if (ev) {
    ev.push({ type: 'BULLDOZE', tick: s.tick, actor: c.index, x: c.x, y: c.y, defender: d.index });
    ev.push({
      type: 'SPECIAL_END',
      tick: s.tick,
      actor: c.index,
      special: SpecialId.BULLDOZER,
      consumed: true,
    });
  }
  knock(s, ev, c, d, B.bounceSpeed, MOTION.bulldozeBounceVy, B.stunFrames);
  addHitStop(s, ev, c.index, HITSTOP.bulldozerBreak, 'bulldoze', c.x, c.y);
  if (lunge !== LungeClass.GRAB) commitFailed(s, ev, d);
};

const truckBreak = (s: SimState, ev: SimEventSink, c: PlayerSim, d: PlayerSim, lunge: number): void => {
  if (ev) ev.push({ type: 'TRUCK_BREAK', tick: s.tick, actor: c.index, x: c.x, y: c.y, defender: d.index });
  knock(s, ev, c, d, HITBOX.truck.knockbackX, HITBOX.truck.knockbackY, FRAMES.stun.truckKnockAside);
  addHitStop(s, ev, c.index, HITSTOP.truckBreak, 'truck', c.x, c.y);
  // The leap ended without tackling: a fake jump right before it baited it (the "fake jump → truck under" read).
  if (lunge !== LungeClass.GRAB) commitFailed(s, ev, d);
};

const stiffArmHit = (s: SimState, ev: SimEventSink, c: PlayerSim, d: PlayerSim): void => {
  c.flags |= PF.STIFF_HIT;
  if (ev) ev.push({ type: 'STIFF_ARM', tick: s.tick, actor: c.index, x: c.x, y: c.y, hit: true });
  knock(s, ev, c, d, HITBOX.stiffArm.push, MOTION.stiffArmPopVy, FRAMES.stun.stiffArmed);
  addHitStop(s, ev, c.index, HITSTOP.stiffArm, 'stiff_arm', c.x, c.y);
};

const spinDodge = (s: SimState, ev: SimEventSink, c: PlayerSim, d: PlayerSim, lunge: number): void => {
  const first = !c.spinDodged;
  c.spinDodged = true;
  d.beatenTick = s.tick; // pursuit grace (MOTION.pursuit.graceFrames)
  if (lunge === LungeClass.GRAB) {
    if (!first) return; // a standing defender overlapping the spin: once per spin
    emitWhiff(s, ev, d, lunge);
    d.beatenFrames = MOTION.beatenMaxFrames; // spun past: he cannot grab again before the carrier is through
    // ...and he grabbed air: a short stumble, so the spin's win is a real step past him (no point-blank dive)
    if (d.grounded) setLock(d, PlayerState.STUMBLE, FRAMES.stun.spunPast);
  } else lungeFails(s, ev, d, lunge);
  if (ev && firstThisStep(Once.SPIN_DODGE, c.index))
    ev.push({ type: 'SPIN_DODGE', tick: s.tick, actor: c.index, x: c.x, y: c.y });
  if (lunge !== LungeClass.GRAB) {
    spinDodgeBait(s, ev, c, d);
    if (d.commitKind !== CommitKind.NONE) commitFailed(s, ev, d);
  }
};

const hurdleClear = (s: SimState, ev: SimEventSink, c: PlayerSim, d: PlayerSim): void => {
  lungeFails(s, ev, d, LungeClass.DIVE);
  d.beatenTick = s.tick; // pursuit grace (MOTION.pursuit.graceFrames)
  if (ev) ev.push({ type: 'HURDLE_CLEAR', tick: s.tick, actor: c.index, x: c.x, y: c.y, defender: d.index });
  commitFailed(s, ev, d);
};

// ── Detection ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Which attack of `d` makes contact with carrier `c` this tick: LungeClass, -1 none, or HURDLE_OVER when an
 * airborne carrier is above a live Dive (no contact; credited as a HURDLE_CLEAR).
 */
const HURDLE_OVER = 99;

const detect = (c: PlayerSim, d: PlayerSim): number => {
  hurtboxOf(c, hurt);
  const lunge = activeLunge(d);
  if (lunge >= 0) {
    lungeBoxOf(d, lunge as LungeClass, lungeBox);
    if (boxesTouch(lungeBox, hurt)) {
      if (lungeReachesHeight(lunge as LungeClass, hurt)) return lunge;
      if (lunge === LungeClass.DIVE && !c.grounded) return HURDLE_OVER;
    } else if (lunge === LungeClass.DIVE && !c.grounded) {
      // A hurdle well above the dive: same column, higher than the box.
      const dx = hurt.cx - lungeBox.cx;
      const w = hurt.rx + lungeBox.rx;
      if (dx < w && dx > -w && hurt.cy > lungeBox.cy) return HURDLE_OVER;
    }
    return -1; // a lunging defender cannot also grab
  }
  if (!canGrab(d)) return -1;
  hurtboxOf(d, lungeBox);
  if (boxesTouch(lungeBox, hurt)) return LungeClass.GRAB;
  if (stiffArmActive(c)) {
    // the Fist reaches a grabber before heads touch
    stiffArmBoxOf(c, hurt);
    if (boxesTouch(lungeBox, hurt)) return LungeClass.GRAB;
  }
  return -1;
};

/**
 * BEATEN bookkeeping: counts down; the carrier himself is never beaten; the defender recovers as soon as the
 * carrier is clear past him in the carrier's attack direction (heads apart by the radii + contactSlop, so the
 * grab cannot fire on the very tick the flag clears).
 */
const updateBeaten = (s: SimState, carrier: number): void => {
  const p0 = s.players[0];
  const p1 = s.players[1];
  if (p0.beatenFrames > 0) p0.beatenFrames--;
  if (p1.beatenFrames > 0) p1.beatenFrames--;
  if (carrier < 0) return;
  const c = s.players[carrier as 0 | 1];
  const d = s.players[carrier === 0 ? 1 : 0];
  c.beatenFrames = 0;
  if (d.beatenFrames === 0) return;
  const past = (c.x - d.x) * carrierAttackDir(s, carrier);
  if (past > c.stats.headRadius + d.stats.headRadius + MOTION.contactSlop) d.beatenFrames = 0;
};

export const resolveContacts = (s: SimState, ev: SimEventSink): void => {
  const r = s.rules;
  if (r.phase !== Phase.LIVE && r.phase !== Phase.RUSH_COUNT) return;
  popDecoys(s, ev);
  const b = s.ball;
  const held = b.state === BallState.HELD && b.holder >= 0;
  updateBeaten(s, held ? b.holder : -1);
  if (r.phase !== Phase.LIVE || !held) return;
  const c = s.players[b.holder as 0 | 1];
  const d = s.players[b.holder === 0 ? 1 : 0];
  if (c.state === PlayerState.TACKLED) return;
  const lunge = detect(c, d);
  if (lunge < 0) return;
  const cc = lunge === HURDLE_OVER ? carrierClassOf(c) : carrierClassAgainst(c, d, lunge as LungeClass);
  if (lunge === HURDLE_OVER) {
    if (cc === CarrierClass.AIR) hurdleClear(s, ev, c, d);
    return;
  }
  const out = counterOutcome(cc, lunge as LungeClass);
  switch (out) {
    case CounterOutcome.TACKLE:
    case CounterOutcome.COUNTER_TACKLE:
      if (lunge !== LungeClass.LEAP && bulldozerActive(c)) bulldoze(s, ev, c, d, lunge);
      else tackle(s, ev, c, d, lunge, out === CounterOutcome.COUNTER_TACKLE, cc);
      return;
    case CounterOutcome.WHIFF:
      if (cc === CarrierClass.SPIN) spinDodge(s, ev, c, d, lunge);
      else hurdleClear(s, ev, c, d);
      return;
    case CounterOutcome.BREAK:
      if (cc === CarrierClass.STIFF_ARM) stiffArmHit(s, ev, c, d);
      else truckBreak(s, ev, c, d, lunge);
      return;
    default:
  }
};
